'use client'
import { motion } from 'framer-motion'
import { useLang } from '../context/LangContext'
import { t, productOrgs, productDevOps } from '../lib/translations'
import SectionHeader from './SectionHeader'
import BenefitsBar from './BenefitsBar'
import FeatureCardGrid from './FeatureCardGrid'
import TrustedBy from './TrustedBy'
import LogosMarquee from './LogosMarquee'
import DevOpsToolsTabs from './DevOpsToolsTabs'
import Icon from './Icon'

const capabilityIcons = ['building', 'shield', 'credit-card', 'shield-check', 'chart-bar', 'clipboard', 'users', 'cloud'] as const

export default function OrganizationsLanding() {
  const lang = useLang()
  const capabilities = productOrgs.capabilities.map((c, i) => ({
    title: t(lang, c.title),
    description: t(lang, c.desc),
    icon: <Icon name={capabilityIcons[i]} className="w-6 h-6" strokeWidth={1.5} />,
  }))
  const benefits = productOrgs.benefits.map((b) => ({ title: t(lang, b.title), desc: t(lang, b.desc) }))

  return (
    <>
      {/* Hero */}
      <section id="organizations" className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#7c3aed]/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[#7c3aed]/15 rounded-full blur-[160px] pointer-events-none opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#7c3aed]/15 rounded-full blur-[140px] pointer-events-none opacity-75" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-[#7c3aed]/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#07070d] via-[#07070d]/80 to-transparent pointer-events-none z-10" />

        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-sm font-medium text-[#818cf8] tracking-wide uppercase">{t(lang, productOrgs.heroKicker)}</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-white">
              {t(lang, productOrgs.heroTitle1)}{' '}
              <span className="gradient-text">{t(lang, productOrgs.heroTitle2)}</span>
            </h1>
            <p className="mt-6 text-lg text-[#94a3b8] leading-relaxed max-w-2xl mx-auto">
              {t(lang, productOrgs.heroDesc)}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#cta"
                className="w-full sm:w-auto text-center px-6 py-3 rounded-lg bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] text-white text-sm font-medium hover:from-[#6366f1] hover:to-[#c084fc] transition-all duration-200 shadow-lg shadow-[#4f46e5]/20 btn-lightning"
              >
                {t(lang, productOrgs.ctaStart)}
              </a>
              <a
                href="#org-capabilities"
                className="w-full sm:w-auto text-center px-6 py-3 rounded-lg border border-white/[0.1] text-[#94a3b8] text-sm font-medium transition-all duration-200 btn-outline"
              >
                {t(lang, productOrgs.ctaExplore)}
              </a>
            </div>

            <TrustedBy className="mt-16" />
          </motion.div>

          <LogosMarquee marginTop="mt-8" />
        </div>
      </section>

      <BenefitsBar items={benefits} />

      {/* Capabilities Grid */}
      <section id="org-capabilities" className="relative py-24 sm:py-32">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <SectionHeader
            heading={`${t(lang, productOrgs.featuresHeading)} `}
            highlighted={t(lang, productOrgs.featuresHighlight)}
            description={t(lang, productOrgs.featuresDesc)}
          />

          <FeatureCardGrid items={capabilities} layout="inline" accent="#7c3aed" accent2="#8b5cf6" />
        </div>
      </section>

      {/* DevOps Tools Tabs */}
      <section className="relative py-24 sm:py-32 bg-white/[0.005]">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <SectionHeader
            heading={`${t(lang, productDevOps.toolsHeading)}`}
            description={t(lang, productDevOps.toolsDesc)}
            highlighted=""
          />
          <DevOpsToolsTabs className="mt-12" />
        </div>
      </section>
    </>
  )
}
